<div class="d-flex flex-wrap justify-content-between align-items-end mb-3">
  <div>
    <p>
      All plans billed monthly
      @if (hasSubscriptionCurrency) {
        <span> in {{ displayCurrency }}. </span>
      }
    </p>
    <p>You can upgrade or downgrade at anytime.</p>
  </div>
</div>

@if (accountantPricing) {
  <app-alert type="info" size="small" class="w-100">
    <p>
      Accounting plans are billed per client register, with each register being
      allocated 25 assets towards a shared asset allowance.
    </p>
    <p>Plans have a 10 register minimum (shared allowance of 250 assets).</p>
  </app-alert>
}

<div class="d-flex justify-content-between mb-3">
  <button
    data-dd-action-name="subscription-packages.toggle-calculator-button"
    type="button"
    class="btn btn-outline"
    (click)="onToggleCalculator()"
  >
    <i-lucide [name]="isCalculatorOpen ? 'x' : 'calculator'"></i-lucide>
    {{ isCalculatorOpen ? 'Close Calculator' : 'Calculate Pricing' }}
  </button>

  @if (showAccountingFirmPlans) {
    <div>
      <input
        data-dd-action-name="subscription-packages.accountant-pricing-button"
        id="accountant-pricing"
        type="checkbox"
        class="switch-input"
        [checked]="accountantPricing"
        (click)="togglePricing()"
      />
      <label for="accountant-pricing" class="switch"> </label>

      <span class="ms-2"> See bulk pricing for accounting firms </span>
    </div>
  }
</div>

@if (isCalculatorOpen) {
  <app-subscription-calculator
    [isAccountantPricing]="accountantPricing"
    [packageType]="selectedPackage"
    [subscription]="subscription"
  ></app-subscription-calculator>
}

@if (upgradeOptions && prices) {
  <div
    [class]="'plans plans-' + ((showFreePlan ? 1 : 0) + 4)"
    [class.calculator-open]="isCalculatorOpen"
  >
    @if (showFreePlan) {
      <div class="card p-4 border plan-free">
        <div class="plan-header">
          <h3>Free</h3>
        </div>

        <div class="pricing">
          <div class="price-display">
            <div class="price">
              <span class="currency-symbol">{{ currencySymbol }}</span
              >0
            </div>
            <div class="price-details">
              <p>up to {{ freePlanMaxAssets }} assets</p>
            </div>
          </div>
        </div>

        @if (canUseFreePlan) {
          <app-pricing-package-actions
            [subscription]="subscription"
            [newOrganisation]="newOrganisation"
            [packageType]="PackageType.Free"
            [isUpdating]="isUpdating"
            (updatePackage)="changePlan($event)"
          ></app-pricing-package-actions>
        }

        @if (isTrialPlan) {
          <div>
            <app-alert type="info" size="small" class="w-100 mt-4">
              <p>
                When your 30 day trial ends, you'll be automatically moved to
                the free plan if you have {{ freePlanMaxAssets }} assets or
                less.
              </p>
              @if (!canUseFreePlan) {
                <p>
                  You currently have {{ subscription.totalAssets }} asset{{
                    subscription.totalAssets === 1 ? '' : 's'
                  }}.
                </p>
              }
            </app-alert>
          </div>
        }

        <div class="feature-lists">
          <h5>Includes all Standard features</h5>
          <ul>
            <li>Try FinQuery Fixed Assets</li>
            <li>No time limit</li>
          </ul>
        </div>
      </div>
    }

    <div
      data-dd-action-name="subscription-packages.lite-plan-button"
      (click)="
        selectedPackage = accountantPricing
          ? PackageType.AccountingV2Lite
          : PackageType.BusinessLiteV2
      "
      class="card p-4 border plan-lite"
      [class.selected]="
        selectedPackage === PackageType.AccountingV2Lite ||
        selectedPackage === PackageType.BusinessLiteV2
      "
    >
      <div class="plan-header">
        <h3>
          <span> Lite </span>

          @if (isCalculatorOpen) {
            <input
              data-dd-action-name="subscription-packages.lite-plan-radio"
              type="radio"
              class="form-check-input"
              [(ngModel)]="selectedPackage"
              [value]="
                accountantPricing
                  ? PackageType.AccountingV2Lite
                  : PackageType.BusinessLiteV2
              "
              name="package"
            />
          }
        </h3>
      </div>

      @if (!accountantPricing) {
        <ng-container
          *ngTemplateOutlet="
            pricing;
            context: { pricing: prices[PackageType.BusinessLiteV2] }
          "
        ></ng-container>
      } @else {
        <ng-container
          *ngTemplateOutlet="
            pricingAccountant;
            context: { pricing: prices[PackageType.AccountingV2Lite] }
          "
        ></ng-container>
      }

      <app-pricing-package-actions
        [subscription]="subscription"
        [newOrganisation]="newOrganisation"
        [packageType]="
          accountantPricing
            ? PackageType.AccountingV2Lite
            : PackageType.BusinessLiteV2
        "
        [isUpdating]="isUpdating"
        (updatePackage)="changePlan($event)"
      ></app-pricing-package-actions>

      <div class="feature-lists">
        <h5>Platform</h5>
        <ul>
          <li>
            Unlimited users
            @if (accountantPricing) {
              *
            }
          </li>
          <li>Xero integration</li>
          <li>QuickBooks Online integration</li>
          <li>Automated journals</li>
        </ul>

        <h5>Assets</h5>
        <ul>
          <li>Taxable use</li>
          <li>Reassessments</li>
          <li>
            Adjustments
            <ng-container
              *ngTemplateOutlet="
                inUseBadge;
                context: { feature: Feature.Adjustments }
              "
            ></ng-container>
          </li>
          <li>
            USA
            <img class="flag" src="/resources/images/flag-us.svg" alt="USA" />
            <ul>
              <li>
                <app-tax-office-abbr [regimeId]="100"></app-tax-office-abbr>
                tax methods
              </li>
              <li>
                <app-tax-office-abbr [regimeId]="100"></app-tax-office-abbr>
                effective life lookup
              </li>
              <li>Section 179</li>
              <li>Bonus depreciation</li>
            </ul>
          </li>
        </ul>
      </div>

      <ng-container
        *ngTemplateOutlet="
          isRecommended;
          context: {
            subscriptionPackage: PackageType.BusinessLite,
          }
        "
      ></ng-container>
    </div>

    <div
      data-dd-action-name="subscription-packages.basic-plan-button"
      (click)="
        selectedPackage = accountantPricing
          ? PackageType.AccountingV2Basic
          : PackageType.BusinessBasicV2
      "
      class="card border plan-basic"
      [class.selected]="
        selectedPackage === PackageType.AccountingV2Basic ||
        selectedPackage === PackageType.BusinessBasicV2
      "
    >
      <div class="plan-header">
        <h3>
          <span> Basic </span>

          @if (isCalculatorOpen) {
            <input
              data-dd-action-name="subscription-packages.basic-plan-radio"
              type="radio"
              class="form-check-input"
              [(ngModel)]="selectedPackage"
              [value]="
                accountantPricing
                  ? PackageType.AccountingV2Basic
                  : PackageType.BusinessBasicV2
              "
              name="package"
            />
          }
        </h3>
      </div>

      @if (!accountantPricing) {
        <ng-container
          *ngTemplateOutlet="
            pricing;
            context: { pricing: prices[PackageType.BusinessBasicV2] }
          "
        ></ng-container>
      } @else {
        <ng-container
          *ngTemplateOutlet="
            pricingAccountant;
            context: { pricing: prices[PackageType.AccountingV2Basic] }
          "
        ></ng-container>
      }

      <app-pricing-package-actions
        [subscription]="subscription"
        [newOrganisation]="newOrganisation"
        [packageType]="
          accountantPricing
            ? PackageType.AccountingV2Basic
            : PackageType.BusinessBasicV2
        "
        [isUpdating]="isUpdating"
        (updatePackage)="changePlan($event)"
      ></app-pricing-package-actions>

      <div class="feature-lists">
        <h5>Lite plus...</h5>

        <h5>Platform</h5>
        <ul>
          <li>
            Classifications (e.g. cost {{ 'centres' | ize }})
            <ng-container
              *ngTemplateOutlet="
                inUseBadge;
                context: { feature: Feature.Classifications }
              "
            ></ng-container>
          </li>
          <li>Custom fields (e.g. serial #s)</li>
          <li>
            Attachments
            <ng-container
              *ngTemplateOutlet="
                inUseBadge;
                context: { feature: Feature.Attachments }
              "
            ></ng-container>
          </li>
        </ul>

        <h5>Assets</h5>
        <ul>
          <li>Quantities</li>
          <li>Partial acquisitions</li>
          <li>
            Partial disposals
            <ng-container
              *ngTemplateOutlet="
                inUseBadge;
                context: { feature: Feature.PartialDisposals }
              "
            ></ng-container>
          </li>
          <li>Additional cost</li>
        </ul>
      </div>

      <ng-container
        *ngTemplateOutlet="
          isRecommended;
          context: {
            subscriptionPackage: PackageType.BusinessBasicV2,
          }
        "
      ></ng-container>
    </div>

    <div
      data-dd-action-name="subscription-packages.standard-plan-button"
      (click)="
        selectedPackage = accountantPricing
          ? PackageType.AccountingV2Standard
          : PackageType.BusinessStandardV2
      "
      class="card p-4 border plan-standard"
      [class.selected]="
        selectedPackage === PackageType.AccountingV2Standard ||
        selectedPackage === PackageType.BusinessStandardV2
      "
    >
      <div class="plan-header">
        <h3>
          <span> Standard </span>

          @if (isCalculatorOpen) {
            <input
              data-dd-action-name="subscription-packages.standard-plan-radio"
              type="radio"
              class="form-check-input"
              [(ngModel)]="selectedPackage"
              [value]="
                accountantPricing
                  ? PackageType.AccountingV2Standard
                  : PackageType.BusinessStandardV2
              "
              name="package"
            />
          }
        </h3>
      </div>

      @if (!accountantPricing) {
        <ng-container
          *ngTemplateOutlet="
            pricing;
            context: { pricing: prices[PackageType.BusinessStandardV2] }
          "
        ></ng-container>
      } @else {
        <ng-container
          *ngTemplateOutlet="
            pricingAccountant;
            context: { pricing: prices[PackageType.AccountingV2Standard] }
          "
        ></ng-container>
      }

      <app-pricing-package-actions
        [subscription]="subscription"
        [newOrganisation]="newOrganisation"
        [packageType]="
          accountantPricing
            ? PackageType.AccountingV2Standard
            : PackageType.BusinessStandardV2
        "
        [isUpdating]="isUpdating"
        (updatePackage)="changePlan($event)"
      ></app-pricing-package-actions>

      <div class="feature-lists">
        <h5>Basic plus...</h5>

        <h5>Platform</h5>
        <ul>
          <li>Bulk actions (e.g. disposals, reassessments, revaluations)</li>
          <li>
            Consolidations (single country)
            <ng-container
              *ngTemplateOutlet="
                inUseBadge;
                context: { feature: Feature.ConsolidatedReports }
              "
            ></ng-container>
          </li>
        </ul>

        <h5>Assets</h5>
        <ul>
          <li>
            Revaluations
            <ng-container
              *ngTemplateOutlet="
                inUseBadge;
                context: { feature: Feature.RevaluationsImpairments }
              "
            ></ng-container>
          </li>
          <li>
            Impairments
            <ng-container
              *ngTemplateOutlet="
                inUseBadge;
                context: { feature: Feature.Impairments }
              "
            ></ng-container>
          </li>
          <li>
            Works in progress (WIP,
            <abbr title="Construction in progress">CIP</abbr>)
          </li>
          <li>
            Components
            <ng-container
              *ngTemplateOutlet="
                inUseBadge;
                context: { feature: Feature.Components }
              "
            ></ng-container>
          </li>
          <li>
            Custom depreciation methods
            <ng-container
              *ngTemplateOutlet="
                inUseBadge;
                context: { feature: Feature.CustomDepreciationMethods }
              "
            ></ng-container>
          </li>
        </ul>
      </div>

      <ng-container
        *ngTemplateOutlet="
          isRecommended;
          context: {
            subscriptionPackage: [
              PackageType.AccountingStandard,
              PackageType.BusinessStandardV2,
            ],
          }
        "
      ></ng-container>
    </div>

    <div
      class="card p-4 border plan-premium"
      [class.free-hidden]="!showFreePlan"
    >
      <div class="plan-header">
        <h3>Premium</h3>
      </div>

      <div class="pricing">
        <div class="price-display">
          <div class="price w-auto poa">
            <abbr title="Price on application">POA</abbr>
          </div>
        </div>
        <div class="price-description">
          @if (!accountantPricing) {
            <p>
              Annual pricing for businesses with >1,000 assets and/or complex
              requirements below
            </p>
          }
          @if (accountantPricing) {
            <p>
              Annual pricing for accounting firms with >100 client registers
              and/or complex requirements below
            </p>
          }
        </div>
      </div>

      <app-pricing-package-actions
        [subscription]="subscription"
        [newOrganisation]="newOrganisation"
        [packageType]="
          accountantPricing
            ? PackageType.AccountingV2Premium
            : PackageType.BusinessPremiumV2
        "
        [isUpdating]="isUpdating"
        (updatePackage)="changePlan($event)"
      ></app-pricing-package-actions>

      <div class="feature-lists">
        <h5>Standard plus...</h5>

        <h5>Platform</h5>
        <ul>
          <li>Sage Intacct integration</li>
          <li>Microsoft Dynamics integration</li>
        </ul>
      </div>

      <ng-container
        *ngTemplateOutlet="
          isRecommended;
          context: {
            subscriptionPackage: [
              PackageType.AccountingV2Premium,
              PackageType.BusinessPremiumV2,
            ],
          }
        "
      ></ng-container>
    </div>
  </div>
}

<div class="row">
  <div class="col">
    @if (!accountantPricing) {
      <p class="mb-0">
        If you have more than 1,000 assets, please talk to us for special
        pricing.
      </p>
    } @else {
      <p>
        If your average register size is more than 25 assets, or you have more
        than 100 registers, please talk to us for special pricing.
      </p>
      <p>
        Example: My firm has 12 client registers with 420 assets between them. I
        have selected the Standard plan. My monthly subscription price = [{{
          currencySymbol
        }}3 x 12] + [(420 - 12 x 25) ÷ 25 x {{ currencySymbol }}3] =
        {{ currencySymbol }}50 per month.
      </p>
      <p class="mb-0">
        * Includes unlimited users within your firm. Read-only access for your
        client’s users.
      </p>
    }
  </div>
</div>

<ng-template #featuresLookups>
  <li>
    <img
      class="flag"
      src="/resources/images/flag-us.svg"
      alt="USA"
    />&nbsp;<app-tax-office-abbr [regimeId]="100"></app-tax-office-abbr>
    effective life lookup
  </li>
</ng-template>

<ng-template #inUseBadge let-feature="feature">
  @if (isFeatureInUse(feature)) {
    <span class="badge rounded-pill bg-success">In use</span>
  }
</ng-template>

<ng-template #isRecommended let-subscriptionPackage="subscriptionPackage">
  @if (isPackageRecommended(subscriptionPackage)) {
    <div class="border border-success rounded text-success p-2 mt-2">
      <div class="d-flex align-items-center">
        <i-lucide name="check"></i-lucide>
        <p class="mb-0 ms-3">Recommended based on your current use</p>
      </div>
    </div>
  }
</ng-template>

<ng-template #pricing let-pricing="pricing">
  <div class="pricing">
    <div class="price-display">
      <div class="price">
        <span class="currency-symbol">{{ currencySymbol }}</span
        >{{ pricing?.assetPrice }}
      </div>
      <div class="price-details">
        <p>per {{ pricing?.assetUnits }} assets</p>
      </div>
    </div>
  </div>
</ng-template>

<ng-template #pricingAccountant let-pricing="pricing">
  <div class="pricing">
    <div class="price-display">
      <div class="price accounting">
        <span class="currency-symbol">{{ currencySymbol }}</span
        >{{ pricing?.assetPrice }}
      </div>
      <div class="price-details">
        <p>per client register</p>
      </div>
    </div>

    <div class="price-display leasing">
      <div class="price accounting">
        <span class="currency-symbol">{{ currencySymbol }}</span
        >{{ pricing?.assetPrice }}
      </div>
      <div class="price-details">
        <p>per {{ pricing?.assetUnits }} assets above shared allowance</p>
      </div>
    </div>
  </div>
</ng-template>
